<template>
  <div class="billing-details-page">
    <div class="billing-switch-wrap">
      <button
        type="button"
        class="switch-btn"
        :class="{ active: billingCycle === 'monthly' }"
        @click="billingCycle = 'monthly'"
      >
        MONTHLY
      </button>
      <button
        type="button"
        class="switch-btn"
        :class="{ active: billingCycle === 'yearly' }"
        @click="billingCycle = 'yearly'"
      >
        YEARLY
      </button>
    </div>

    <b-row class="plan-row">
      <b-col
        v-for="plan in plans"
        :key="plan.id"
        md="4"
        class="mb-2 mb-md-0"
      >
        <b-card
          class="plan-card"
          :class="{ 'popular-card': plan.is_popular }"
        >
          <span
            v-if="plan.is_popular"
            class="popular-tag"
          >
            Popular
          </span>
          <div class="plan-icon">
            <img
              :src="planIcon(plan.name)"
              :alt="`${plan.name} plan`"
              class="plan-icon-img"
            >
          </div>
          <h3>{{ plan.name }}</h3>
          <p class="plan-subtitle">
            {{ planSubtitle(plan.name) }}
          </p>
          <div class="plan-price-wrap">
            <span class="currency">$</span>
            <span class="price">{{ formatPlanPrice(plan.price) }}</span>
            <span class="duration">/ {{ isMonthly ? 'month' : 'year' }}</span>
          </div>
          <ul class="plan-features">
            <li
              v-for="(feature, idx) in plan.features"
              :key="`${plan.id}-f-${idx}`"
            >
              {{ feature }}
            </li>
          </ul>
          <b-button
            class="plan-btn"
            :disabled="checkoutLoading || plan.is_current"
            @click="startPlanUpgrade(plan)"
          >
            <b-spinner
              v-if="checkoutLoading && selectedPlanId === plan.id"
              small
              class="mr-50"
            />
            {{ plan.is_current ? 'Current Plan' : `Upgrade with ${plan.name}` }}
          </b-button>
        </b-card>
      </b-col>
    </b-row>

    <vendor-outcome-modal
      :visible.sync="showUpgradeSuccess"
      variant="plan-upgraded"
      @primary="onUpgradeSuccessPrimary"
    />

  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BSpinner,
} from 'bootstrap-vue'
import VendorOutcomeModal from '@/components/vendor/VendorOutcomeModal.vue'
import { vendorPanelApi } from '@/services/vendorPanelApi'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BSpinner,
    VendorOutcomeModal,
  },
  data() {
    return {
      billingCycle: 'monthly',
      checkoutLoading: false,
      selectedPlanId: null,
      showUpgradeSuccess: false,
      plans: [],
    }
  },
  mounted() {
    this.fetchPlans()
    this.handleBillingReturn()
  },
  computed: {
    isMonthly() {
      return this.billingCycle === 'monthly'
    },
  },
  methods: {
    async fetchPlans() {
      try {
        const response = await vendorPanelApi.plans({ billing_cycle: this.billingCycle })
        this.plans = response?.data?.data?.plans || []
      } catch (error) {
        this.plans = []
      }
    },
    planIcon(name) {
      const key = String(name || '').toLowerCase()
      if (key.includes('standard')) return '/svg/wallet/Standard.svg'
      if (key.includes('enterprise')) return '/svg/wallet/Enterprise.svg'
      return '/svg/wallet/Basic.svg'
    },
    planSubtitle(name) {
      const key = String(name || '').toLowerCase()
      if (key.includes('standard')) return 'For small to medium businesses'
      if (key.includes('enterprise')) return 'Solution for big organizations'
      return 'A simple start for everyone'
    },
    formatPlanPrice(price) {
      const n = Number(price)
      return Number.isFinite(n) ? n : 0
    },
    async handleBillingReturn() {
      const billing = this.$route?.query?.billing
      const sessionId = this.$route?.query?.session_id
      if (billing === 'success' && sessionId) {
        try {
          await vendorPanelApi.confirmPlanCheckout({ session_id: sessionId })
          this.showUpgradeSuccess = true
        } catch (e) {
          this.$toast?.error?.(e?.response?.data?.message || 'Could not confirm payment.')
        }
        await this.fetchPlans()
        this.$router.replace({ query: {} })
      } else if (billing === 'cancelled') {
        this.$toast?.info?.('Payment cancelled.')
        this.$router.replace({ query: {} })
      }
    },
    onUpgradeSuccessPrimary() {
      this.showUpgradeSuccess = false
      this.$router.push({ name: 'Plan Renewal' })
    },
    async startPlanUpgrade(plan) {
      if (!plan || plan.is_current) return
      this.checkoutLoading = true
      this.selectedPlanId = plan.id
      try {
        const price = Number(plan.price || 0)
        if (price <= 0) {
          await vendorPanelApi.upgradePlan({
            plan_id: plan.id,
            billing_cycle: this.billingCycle,
            payment_method: 'stripe',
          })
          await this.fetchPlans()
          this.showUpgradeSuccess = true
          return
        }
        const { data } = await vendorPanelApi.createPlanCheckout({
          plan_id: plan.id,
          billing_cycle: this.billingCycle,
        })
        const checkoutUrl = data?.data?.checkout_url
        if (checkoutUrl) {
          window.location.href = checkoutUrl
          return
        }
        this.$toast?.error?.('Checkout URL was not returned.')
      } catch (e) {
        this.$toast?.error?.(e?.response?.data?.message || 'Unable to start checkout.')
      } finally {
        this.checkoutLoading = false
        this.selectedPlanId = null
      }
    },
  },
  watch: {
    billingCycle() {
      this.fetchPlans()
    },
  },
}
</script>

<style scoped lang="scss">
.billing-details-page {
  .billing-switch-wrap {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    margin-bottom: 2.1rem;
  }

  .switch-btn {
    border: 0;
    background: transparent;
    color: #a39fb4;
    font-size: 0.68rem;
    font-weight: 700;
    border-radius: 20px;
    padding: 0.55rem 1.4rem;
    letter-spacing: 0.02em;
  }

  .switch-btn.active {
    background: #011425;
    color: #fff;
    box-shadow: 0 4px 12px rgba(1, 20, 37, 0.2);
  }

  .plan-row {
    max-width: 920px;
    margin: 0 auto;
  }

  .plan-card {
    border: 1px solid #ececf3;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(36, 44, 58, 0.06);
    text-align: center;
    height: 100%;
    position: relative;

    ::v-deep .card-body {
      padding: 1.35rem 1.15rem 1.1rem;
    }

    h3 {
      color: #4b465c;
      font-size: 2rem;
      margin-bottom: 0.25rem;
      font-weight: 600;
    }
  }

  .popular-card {
    border-color: #31354f;
  }

  .popular-tag {
    position: absolute;
    top: 12px;
    right: 14px;
    display: inline-block;
    background: #ebe8f4 !important;
    color: #011425 !important;
    font-size: 0.72rem !important;
    font-weight: 700 !important;
    border-radius: 9999px !important;
    padding: 0.3rem 0.85rem !important;
    line-height: 1.2 !important;
    letter-spacing: 0.01em;
    white-space: nowrap;
  }

  .plan-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.8rem;
    min-height: 64px;
  }

  .plan-icon-img {
    width: 64px;
    height: 64px;
    object-fit: contain;
    display: block;
  }

  .plan-subtitle {
    color: #8b879e;
    min-height: 44px;
    margin-bottom: 0.9rem;
  }

  .plan-price-wrap {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    gap: 0.2rem;
    margin-bottom: 0.85rem;
  }

  .currency {
    font-size: 1.75rem;
    color: #0f172a;
    line-height: 1.2;
  }

  .price {
    font-size: 4rem;
    color: #0f172a;
    font-weight: 700;
    line-height: 0.95;
  }

  .duration {
    color: #7f7a93;
    font-size: 1.2rem;
    margin-bottom: 0.52rem;
  }

  .plan-features {
    text-align: left;
    padding-left: 1.15rem;
    color: #64607a;
    margin-bottom: 1rem;

    li {
      margin-bottom: 0.44rem;
      font-size: 0.95rem;
    }
  }

  .plan-btn {
    width: 100%;
    background: #011425 !important;
    border-color: #011425 !important;
    color: #fff !important;
    font-weight: 600;
  }
}
</style>

